import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDevFavicon } from '~/hooks/use-dev-favicon';

export function EnvironmentBanner(): React.ReactNode {
  if (import.meta.env.MODE === 'production') return null;
  return <BannerInner />;
}

function BannerInner(): React.ReactNode {
  const { t } = useTranslation();
  useDevFavicon();

  const [position, setPosition] = useState<{ x: number; y: number }>(() => ({
    x: window.innerWidth / 2,
    y: 16
  }));
  const [isWalking, setIsWalking] = useState(false);
  const [legFrame, setLegFrame] = useState(0);
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const walkAnimRef = useRef<number | null>(null);
  const legAnimRef = useRef<number | null>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  const stopWalking = useCallback((): void => {
    if (walkAnimRef.current !== null) {
      cancelAnimationFrame(walkAnimRef.current);
      walkAnimRef.current = null;
    }
    if (legAnimRef.current !== null) {
      clearInterval(legAnimRef.current);
      legAnimRef.current = null;
    }
    setIsWalking(false);
    setLegFrame(0);
  }, []);

  const startWalking = useCallback((): void => {
    if (isWalking || isDragging.current || didDrag.current) return;
    setIsWalking(true);

    legAnimRef.current = window.setInterval(() => {
      setLegFrame((f) => (f + 1) % 4);
    }, 120);

    const bannerWidth = bannerRef.current?.offsetWidth ?? 100;
    const padding = 16;
    const targetX = window.innerWidth - bannerWidth / 2 - padding;
    const speed = 2.5;

    function step(): void {
      setPosition((prev) => {
        const newX = prev.x + speed;
        if (newX >= targetX) {
          stopWalking();
          return { x: targetX, y: prev.y };
        }
        return { ...prev, x: newX };
      });
      walkAnimRef.current = requestAnimationFrame(step);
    }

    walkAnimRef.current = requestAnimationFrame(step);
  }, [isWalking, stopWalking]);

  useEffect(() => {
    function onMouseMove(e: MouseEvent): void {
      if (!isDragging.current) return;
      didDrag.current = true;
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y
      });
    }

    function onMouseUp(): void {
      isDragging.current = false;
      setTimeout(() => {
        didDrag.current = false;
      }, 0);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (walkAnimRef.current !== null) cancelAnimationFrame(walkAnimRef.current);
      if (legAnimRef.current !== null) clearInterval(legAnimRef.current);
    };
  }, []);

  const isTest = import.meta.env.MODE === 'test';

  function onMouseDown(e: React.MouseEvent<HTMLDivElement>): void {
    if (isWalking) return;
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  }

  function onTouchStart(e: React.TouchEvent<HTMLDivElement>): void {
    if (isWalking) return;
    const touch = e.touches[0];
    if (!touch) return;
    isDragging.current = true;
    dragOffset.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    };
  }

  function onTouchMove(e: React.TouchEvent<HTMLDivElement>): void {
    if (!isDragging.current) return;
    didDrag.current = true;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    setPosition({
      x: touch.clientX - dragOffset.current.x,
      y: touch.clientY - dragOffset.current.y
    });
  }

  function onTouchEnd(): void {
    isDragging.current = false;
    setTimeout(() => {
      didDrag.current = false;
    }, 0);
  }

  const legPositions: Array<[number, number]> = [
    [-8, 6],
    [-4, 10],
    [4, 10],
    [8, 6]
  ];
  const leftLeg = legPositions[legFrame] ?? [-8, 6];
  const rightLeg = legPositions[(legFrame + 2) % 4] ?? [4, 10];

  return (
    <div
      className="fixed z-50 -translate-x-1/2 select-none"
      style={{ left: position.x, top: position.y }}
    >
      <div
        ref={bannerRef}
        className={`cursor-grab rounded-full px-4 py-1.5 text-xs font-semibold shadow-md active:cursor-grabbing ${
          isTest ? 'bg-blue-500 text-white' : 'bg-amber-400 text-amber-950'
        } ${isWalking ? 'cursor-default' : ''}`}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={startWalking}
      >
        {isTest ? t('environmentBanner.test') : t('environmentBanner.development')}
      </div>
      {isWalking && (
        <svg
          width="40"
          height="20"
          viewBox="-20 0 40 20"
          className="absolute left-1/2 -translate-x-1/2"
          style={{ top: '100%' }}
        >
          {/* Left leg */}
          <line
            x1="0"
            y1="0"
            x2={leftLeg[0]}
            y2={leftLeg[1]}
            stroke={isTest ? '#3b82f6' : '#d97706'}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          {/* Left foot */}
          <circle cx={leftLeg[0]} cy={leftLeg[1]} r="2" fill={isTest ? '#3b82f6' : '#d97706'} />
          {/* Right leg */}
          <line
            x1="0"
            y1="0"
            x2={rightLeg[0]}
            y2={rightLeg[1]}
            stroke={isTest ? '#3b82f6' : '#d97706'}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          {/* Right foot */}
          <circle cx={rightLeg[0]} cy={rightLeg[1]} r="2" fill={isTest ? '#3b82f6' : '#d97706'} />
        </svg>
      )}
    </div>
  );
}
